// frontend/src/context/NotificationContext.jsx
//
// Admin-side notification state. Connects to the backend Socket.IO server
// when an admin/staff user is authenticated, fetches the recent list +
// unread count via REST, and listens for live `notification:new` events.
//
// Exposes via useNotifications():
//   {
//     items, unreadCount, connected, loading,
//     markRead(id), markAllRead(), refresh(),
//     audioOn, setAudioOn, // user toggle for the chime
//   }

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import { useAuth } from './index';
import {
  ding,
  unlockAudio,
  startTabFlash,
  stopTabFlash,
} from '../utils/notificationFx';
import {
  isPushSupported,
  notificationPermission,
  isCurrentlySubscribed,
  subscribePush,
  unsubscribePush,
  registerServiceWorker,
} from '../utils/pushClient';

const NotificationContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// AUDIT FIX (H-11): inline helper so every fetch carries credentials +
// the CSRF header on state-changing requests.
function _readCsrfCookie() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)scr_csrf=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}
let _csrfCache = null;
async function _primeCsrf(baseUrl) {
  if (_csrfCache) return _csrfCache;
  try {
    const r = await fetch(`${baseUrl}/csrf`, { credentials: 'include' });
    if (r.ok) {
      const j = await r.json();
      _csrfCache = (j && j.csrfToken) || _readCsrfCookie();
    }
  } catch (_) { _csrfCache = _readCsrfCookie(); }
  return _csrfCache;
}
async function __secureFetch(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const isWrite = !['GET', 'HEAD', 'OPTIONS'].includes(method);
  let token = _csrfCache || _readCsrfCookie();
  if (isWrite && !token) {
    const baseMatch = url.match(/^(https?:\/\/[^/]+\/api)/) || [];
    if (baseMatch[1]) token = await _primeCsrf(baseMatch[1]);
  }
  const finalOpts = {
    ...options,
    credentials: 'include',
    headers: {
      ...(options.headers || {}),
      ...(token && isWrite ? { 'X-CSRF-Token': token } : {}),
    },
  };
  return fetch(url, finalOpts);
}
// Socket.IO connects to the bare host (without /api)
const SOCKET_URL = API_URL.replace(/\/api\/?$/, '');

// localStorage key for the user's "play sound" preference
const AUDIO_PREF_KEY = 'admin.notifications.audioOn';

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  // Notification bell is admin/manager/cashier only — chef and waiter have
  // their own role-specific live views (kitchen / floor) and don't need a
  // global notification stream cluttering their UI. Variable name stays as
  // 'isAdmin' to minimise churn but the meaning is "can receive admin bell".
  const NOTIF_ROLES = ['admin', 'manager', 'cashier'];
  const isAdmin = !!user && NOTIF_ROLES.includes(user.role);

  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);

  const [audioOn, setAudioOnState] = useState(() => {
    try {
      const v = localStorage.getItem(AUDIO_PREF_KEY);
      return v === null ? true : v === '1';
    } catch {
      return true;
    }
  });

  // Push subscription state
  const [pushSupported] = useState(() => {
    try { return isPushSupported(); } catch { return false; }
  });
  const [pushPermission, setPushPermission] = useState(() => {
    try { return notificationPermission(); } catch { return 'default'; }
  });
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const setAudioOn = useCallback((v) => {
    setAudioOnState(v);
    try {
      localStorage.setItem(AUDIO_PREF_KEY, v ? '1' : '0');
    } catch {
      /* ignore quota errors */
    }
  }, []);

  // ---------- Push subscribe / unsubscribe ----------

  const enablePush = useCallback(async () => {
    if (!isAdmin || !pushSupported) return false;
    setPushBusy(true);
    try {
      const sub = await subscribePush();
      const ok = !!sub;
      setPushSubscribed(ok);
      setPushPermission(notificationPermission());
      return ok;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[notifications] enablePush failed:', e.message);
      return false;
    } finally {
      setPushBusy(false);
    }
  }, [isAdmin, pushSupported]);

  const disablePush = useCallback(async () => {
    if (!pushSupported) return;
    setPushBusy(true);
    try {
      await unsubscribePush();
      setPushSubscribed(false);
    } finally {
      setPushBusy(false);
    }
  }, [pushSupported]);

  const socketRef = useRef(null);

  // ---------- REST helpers ----------

  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const res = await __secureFetch(`${API_URL}/admin/notifications?limit=30`, {
        headers: authHeaders(),
      });
      const json = await res.json();
      if (json?.success) {
        setItems(json.data?.items || []);
        setUnreadCount(json.data?.unreadCount || 0);
      }
    } catch (e) {
      // Network error — leave previous state alone
      // eslint-disable-next-line no-console
      console.warn('[notifications] refresh failed:', e.message);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  const markRead = useCallback(
    async (id) => {
      if (!id || !isAdmin) return;
      // Optimistic update
      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      try {
        const res = await __secureFetch(`${API_URL}/admin/notifications/${id}/read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
        });
        const json = await res.json();
        if (json?.success && typeof json.data?.unreadCount === 'number') {
          setUnreadCount(json.data.unreadCount);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[notifications] markRead failed:', e.message);
      }
    },
    [isAdmin]
  );

  const markAllRead = useCallback(async () => {
    if (!isAdmin) return;
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await __secureFetch(`${API_URL}/admin/notifications/read-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[notifications] markAllRead failed:', e.message);
    }
  }, [isAdmin]);

  // ---------- Service worker registration ----------

  useEffect(() => {
    if (!isAdmin || !pushSupported) return;
    let cancelled = false;
    (async () => {
      try {
        await registerServiceWorker();
        if (cancelled) return;
        const sub = await isCurrentlySubscribed();
        if (!cancelled) setPushSubscribed(sub);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[notifications] SW register check failed:', e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, pushSupported]);

  // ---------- Socket lifecycle ----------

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) {
      // Clean up if user logs out or loses admin role
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setConnected(false);
      setItems([]);
      setUnreadCount(0);
      stopTabFlash();
      return undefined;
    }

    // Initial REST load
    refresh();

    const token = localStorage.getItem('authToken');
    const socket = io(SOCKET_URL, {
      path: '/socket.io',
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1500,
      reconnectionDelayMax: 10000,
    });
    socketRef.current = socket;

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', (err) => {
      // eslint-disable-next-line no-console
      console.warn('[notifications] socket error:', err.message);
      setConnected(false);
    });

    socket.on('notification:new', (n) => {
      if (!n || !n.id) return;

      // Insert at top, dedupe by id
      setItems((prev) => {
        if (prev.some((x) => x.id === n.id)) return prev;
        return [{ ...n, read: false }, ...prev].slice(0, 50);
      });
      setUnreadCount((c) => c + 1);

      // Effects: toast + tab flash. The persistent chime is handled by
      // the unreadCount alarm-loop effect below; firing one here too
      // would cause a double-ring on every new notification.
      toast(n.title || 'New notification', {
        toastId: `notif-${n.id}`,
        autoClose: 5000,
      });
      if (typeof document !== 'undefined' && document.hidden) {
        startTabFlash(1); // exact count refreshed below by the focus handler
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAdmin, refresh]);

  // ---------- Persistent alarm loop ----------
  //
  // Matches the kitchen / floor pattern: while there is at least one
  // unread notification AND audio is on, ding() every ~6 seconds. The
  // first ding fires immediately because the effect re-runs whenever
  // unreadCount transitions from 0 -> >0. Clears itself when the count
  // hits zero (mark-read / mark-all-read) or audio is muted.
  const NOTIF_ALARM_INTERVAL_MS = 6000;
  useEffect(() => {
    if (!isAdmin)            return undefined;
    if (unreadCount === 0)   return undefined;
    if (!audioOn)            return undefined;
    ding();
    const id = setInterval(() => ding(), NOTIF_ALARM_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isAdmin, unreadCount, audioOn]);

  // ---------- Tab focus / flash sync ----------

  useEffect(() => {
    const onFocus = () => {
      stopTabFlash();
    };
    const onVisibility = () => {
      if (document.hidden && unreadCount > 0) {
        startTabFlash(unreadCount);
      } else {
        stopTabFlash();
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [unreadCount]);

  // ---------- Audio unlock on first user interaction ----------

  useEffect(() => {
    if (!isAdmin) return undefined;
    const handler = () => unlockAudio();
    window.addEventListener('click', handler, { once: true, passive: true });
    window.addEventListener('keydown', handler, { once: true, passive: true });
    return () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('keydown', handler);
    };
  }, [isAdmin]);

  const value = {
    items,
    unreadCount,
    connected,
    loading,
    refresh,
    markRead,
    markAllRead,
    audioOn,
    setAudioOn,
    // push
    pushSupported,
    pushPermission,
    pushSubscribed,
    pushBusy,
    enablePush,
    disablePush,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    // Returning a no-op shape lets non-admin pages call useNotifications()
    // without crashing if they happen to be outside the provider.
    return {
      items: [],
      unreadCount: 0,
      connected: false,
      loading: false,
      refresh: () => {},
      markRead: () => {},
      markAllRead: () => {},
      audioOn: false,
      setAudioOn: () => {},
      pushSupported: false,
      pushPermission: 'default',
      pushSubscribed: false,
      pushBusy: false,
      enablePush: async () => false,
      disablePush: async () => {},
    };
  }
  return ctx;
};

export default NotificationContext;
